/**
 * One notification poll for the whole app.
 *
 * The bell in the workspace bar and the workspaces underneath it need the
 * same answer to the same question, and a second poller would double the
 * requests to say the same thing twice. So the polling lives here, once, and
 * everything reads it.
 *
 * == What a workspace uses it for ========================================
 *
 * `useHandoffSignal()` returns a value that changes exactly when something
 * new arrived. A shell watches it and re-reads its lists:
 *
 *     const signal = useHandoffSignal();
 *     useEffect(() => { if (signal) loadView(); }, [signal]);
 *
 * That is the whole of "refresh the tables after the notification". The
 * board does not poll on a timer of its own — a workspace re-reads because
 * something happened, not because time passed.
 *
 * == Outside a session ===================================================
 *
 * Nothing polls until somebody is signed in, and the state resets when they
 * are not. A provider mounted around the whole app must be harmless on the
 * public marketing pages, so it is.
 */
import React, { createContext, useContext, useMemo } from 'react';
import { useOptionalUser } from './UserContext';
import { NotificationState, useNotifications } from '../services/notificationsApi';

const EMPTY: NotificationState = {
  feed: null,
  latestAt: null,
  refresh: async () => undefined,
  markRead: async () => undefined,
  markAllRead: async () => undefined,
  ready: false,
};

const Ctx = createContext<NotificationState>(EMPTY);

export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  const session = useOptionalUser();
  const signedIn = Boolean(session && session.isAuthenticated);
  const state = useNotifications(signedIn);
  const value = useMemo(() => (signedIn ? state : EMPTY), [signedIn, state]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** The feed, for the bell. */
export function useNotificationFeed(): NotificationState {
  return useContext(Ctx);
}

/**
 * A value that changes when work crossed to this person.
 *
 * Null until the first poll answers, so a shell does not re-read its lists
 * once for nothing the moment it mounts.
 */
export function useHandoffSignal(): string | null {
  return useContext(Ctx).latestAt;
}
